import path from 'path';
import { convertToGithubLayout, getPageCodeFilePath } from './utils/veloUtils.js';
import { foldersType } from '../types.js';
const readAllFilesInDir = async (state, dir, folderType) => {
    const entries = await state.fileSystem.readFolder(dir);
    const filePromises = entries.map(async (entry) => {
        const entryPath = path.join(dir, entry.name);
        if (entry.isFile()) {
            return {
                path: convertToGithubLayout(state, entryPath, folderType),
                content: await state.fileSystem.readFile(entryPath)
            };
        }
        return readAllFilesInDir(state, entryPath, folderType);
    });
    return (await Promise.all(filePromises)).flat();
};
const readBackendFiles = async (state) => readAllFilesInDir(state, state.fileStructure.getBackendFolderPath(), foldersType.backend);
const readPublicFiles = async (state) => readAllFilesInDir(state, state.fileStructure.getPublicFolderPath(), foldersType.public);
const readStylesFiles = async (state) => readAllFilesInDir(state, state.fileStructure.getStylesFolderPath(), foldersType.styles);
const readPageCodeFiles = async (state) => {
    const codeFiles = [];
    const pages = state.pages.getAll();
    for (const { pageId, pagePath } of pages) {
        const page = state.fileStructure.getPageFilesPaths(pagePath);
        const content = await state.fileSystem.readFile(page.code);
        codeFiles.push({ path: getPageCodeFilePath(pageId), content });
    }
    return codeFiles;
};
export const getSiteCode = async (state) => {
    const allFiles = await Promise.all([
        readBackendFiles(state),
        readPublicFiles(state),
        readStylesFiles(state),
        readPageCodeFiles(state)
    ]);
    return {
        files: allFiles.flat(),
        layout: 'GITHUB',
        ignoreForbiddenPaths: true
    };
};
const onlinePathRegex = /^(public\/pages|public|backend|styles)\/?((.+)\.js|.*)$/;
/**
 * This api takes an online path and splits it to 3 parts:
 * 1. The folder (public / backend / pages)
 * 2. The relative file path from this folder
 * 3. Page id (only when the folder is pages)
 */
const splitOnlinePath = (onlinePath) => {
    const [, folderPath, relativeFilePath, pageId] = onlinePathRegex.exec(onlinePath) ?? [];
    return {
        folderPath,
        relativeFilePath,
        pageId
    };
};
const buildCodeFilePath = (folderPath, relativeFilePath) => {
    if (!relativeFilePath) {
        return folderPath;
    }
    return path.join(folderPath, relativeFilePath);
};
const buildPageCodeFilePath = ({ pages, fileStructure }, pageId) => {
    if (!pageId) {
        throw new Error('Invalid page code file path');
    }
    const pageFolderPath = pages.getFolderPath(pageId);
    if (!pageFolderPath) {
        throw new Error('Page does not exist');
    }
    return fileStructure.getPageFilesPaths(pageFolderPath).code;
};
const convertOnlineLayoutToRelativePath = (state, onlinePath) => {
    const { fileStructure } = state;
    const { folderPath, relativeFilePath, pageId } = splitOnlinePath(onlinePath);
    switch (folderPath) {
        case 'public':
            return buildCodeFilePath(fileStructure.getPublicFolderPath(), relativeFilePath);
        case 'backend':
            return buildCodeFilePath(fileStructure.getBackendFolderPath(), relativeFilePath);
        case 'styles':
            return buildCodeFilePath(fileStructure.getStylesFolderPath(), relativeFilePath);
        case 'public/pages':
            return buildPageCodeFilePath(state, pageId);
        default:
            throw new Error('Unknown online path');
    }
};
export const convertOnlineLayout = (state, basePath, onlinePath) => path.join(basePath, convertOnlineLayoutToRelativePath(state, onlinePath));
